import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Select, SelectItem, Checkbox, Chip } from '@nextui-org/react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { CtyButton } from '../button/CtyButton';
import { CtyShowToast, checkType } from '../toast/CtyToast';

export interface VersionCompareOption {
  version_id?: string;
  version_name?: string;
  status?: string;
  config_id?: string;
  configuration_id?: string;
  aiworker_id?: string;
  version?: number;
  [key: string]: unknown;
}

type CompareKind = 'added' | 'removed' | 'modified' | 'unchanged';

interface VersionDiffRow {
  key: string;
  leftValue: string;
  rightValue: string;
  kind: CompareKind;
}

interface CompareTreeNode {
  name: string;
  path: string;
  leftValue?: string;
  rightValue?: string;
  kind: CompareKind;
  children: CompareTreeNode[];
}

export interface VersionCompareModalProps<TVersion extends VersionCompareOption = VersionCompareOption> {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly versions: TVersion[];
  readonly fetchVersionData: (version: TVersion, versionId: string) => Promise<Record<string, string>>;
  readonly initialLeftVersionId?: string;
  readonly initialRightVersionId?: string;
  readonly autoRunKey?: number;
  readonly title?: string;
  readonly description?: string;
  readonly getVersionKey?: (version: TVersion) => string;
  readonly getNodeDisplayName?: (nodeName: string) => string;
}

function flattenObject(obj: Record<string, unknown>, prefix = ''): Record<string, string> {
  const out: Record<string, string> = {};
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return out;
  }
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    const path = prefix ? `${prefix}.${key}` : key;
    if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
      Object.assign(out, flattenObject(value as Record<string, unknown>, path));
    } else {
      out[path] = typeof value === 'string' ? value : JSON.stringify(value);
    }
  }
  return out;
}

export function normalizeConfigurationDataForCompare(versionData: unknown): Record<string, string> {
  if (!versionData || typeof versionData !== 'object') return {};
  const wrapped = versionData as {
    configurations?: Array<{ config?: unknown }>;
    data?: { configurations?: Array<{ config?: unknown }>; bot_data?: Record<string, unknown>; workflow_data?: Record<string, unknown>; dify_config?: Record<string, unknown> };
  };

  const toObject = (value: unknown): Record<string, unknown> => {
    if (!value) return {};
    if (typeof value === 'object' && !Array.isArray(value)) return value as Record<string, unknown>;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : {};
      } catch {
        return {};
      }
    }
    return {};
  };

  const firstConfiguration =
    wrapped.configurations?.[0] ??
    wrapped.data?.configurations?.[0] ??
    null;
  const configRoot = firstConfiguration ? toObject(firstConfiguration.config) : toObject(wrapped.data);

  const botRaw = toObject(configRoot?.bot_data);
  const workflowRaw = toObject(configRoot?.workflow_data);
  const difyRaw = toObject(configRoot?.dify_config);
  const botData = toObject(botRaw?.config ?? botRaw);
  const workflowData = toObject(workflowRaw?.config ?? workflowRaw);
  const difyData = toObject(difyRaw?.config ?? difyRaw);

  const bot = flattenObject(botData, 'bot_data');
  const workflow = flattenObject(workflowData, 'workflow_data');
  const dify = flattenObject(difyData, 'dify_config');
  return { ...bot, ...workflow, ...dify };
}

export function parseVersionIdForDisplay(versionId: string | undefined): { versionNumber: string; dateTime: string } {
  if (!versionId || typeof versionId !== 'string') return { versionNumber: '', dateTime: '' };
  const normalized = versionId.trim();
  const compactPattern = /^v?(\d+)\.(\d{8})\.(\d{6})$/i;
  const compactMatch = normalized.match(compactPattern);
  if (compactMatch) {
    const [, versionNum, ddmmyyyy, hhmmss] = compactMatch;
    const dd = ddmmyyyy.slice(0, 2);
    const mm = ddmmyyyy.slice(2, 4);
    const yyyy = ddmmyyyy.slice(4, 8);
    const hh = hhmmss.slice(0, 2);
    const min = hhmmss.slice(2, 4);
    const ss = hhmmss.slice(4, 6);
    return { versionNumber: `v${versionNum}`, dateTime: `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}` };
  }
  const parts = normalized.split('.');
  if (parts.length >= 7) {
    const versionNumber = parts[0];
    const [dd, mm, yyyy, hh, min, ss] = parts.slice(1, 7);
    return { versionNumber, dateTime: `${dd}-${mm}-${yyyy} ${hh}:${min}:${ss}` };
  }
  return { versionNumber: normalized, dateTime: '' };
}

function deriveNodeKindFromChildren(children: CompareTreeNode[]): CompareKind {
  if (!children.length) return 'unchanged';
  const childKinds = Array.from(new Set(children.map((child) => child.kind)));
  if (childKinds.length === 1) return childKinds[0];
  return 'modified';
}

function buildCompareTree(rows: VersionDiffRow[]): CompareTreeNode[] {
  const roots: CompareTreeNode[] = [];
  const ensureNode = (children: CompareTreeNode[], name: string, path: string): CompareTreeNode => {
    const existing = children.find((node) => node.name === name && node.path === path);
    if (existing) return existing;
    const created: CompareTreeNode = { name, path, kind: 'unchanged', children: [] };
    children.push(created);
    return created;
  };

  for (const row of rows) {
    const parts = row.key.split('.');
    let currentChildren = roots;
    let currentPath = '';
    for (let idx = 0; idx < parts.length; idx += 1) {
      const part = parts[idx];
      currentPath = currentPath ? `${currentPath}.${part}` : part;
      const node = ensureNode(currentChildren, part, currentPath);
      if (idx === parts.length - 1) {
        node.leftValue = row.leftValue;
        node.rightValue = row.rightValue;
        node.kind = row.kind;
      }
      currentChildren = node.children;
    }
  }

  const sortAndFinalize = (nodes: CompareTreeNode[]) => {
    nodes.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    nodes.forEach((node) => {
      if (node.children.length > 0) {
        sortAndFinalize(node.children);
        node.kind = deriveNodeKindFromChildren(node.children);
      }
    });
  };
  sortAndFinalize(roots);
  return roots;
}

export function VersionCompareModal<TVersion extends VersionCompareOption = VersionCompareOption>(props: VersionCompareModalProps<TVersion>) {
  const {
    isOpen,
    onClose,
    versions,
    fetchVersionData,
    initialLeftVersionId,
    initialRightVersionId,
    autoRunKey,
    title,
    description,
    getVersionKey: getVersionKeyProp,
    getNodeDisplayName: getNodeDisplayNameProp,
  } = props;
  const { t } = useTranslation();

  const [compareLeftVersionId, setCompareLeftVersionId] = useState<string>('');
  const [compareRightVersionId, setCompareRightVersionId] = useState<string>('');
  const [compareLeftData, setCompareLeftData] = useState<Record<string, string>>({});
  const [compareRightData, setCompareRightData] = useState<Record<string, string>>({});
  const [isComparingVersions, setIsComparingVersions] = useState<boolean>(false);
  const [showOnlyDifferences, setShowOnlyDifferences] = useState<boolean>(true);
  const [openCompareNodes, setOpenCompareNodes] = useState<Record<string, boolean>>({});

  const getVersionKey = useCallback(
    (version: TVersion) => getVersionKeyProp?.(version) ?? (version.version_id ?? version.version_name ?? ''),
    [getVersionKeyProp]
  );

  useEffect(() => {
    if (!isOpen) return;
    setShowOnlyDifferences(true);
    setCompareLeftData({});
    setCompareRightData({});
    setCompareLeftVersionId(initialLeftVersionId ?? '');
    setCompareRightVersionId(initialRightVersionId ?? '');
  }, [isOpen, initialLeftVersionId, initialRightVersionId]);

  const compareRows = useMemo<VersionDiffRow[]>(() => {
    const allKeys = Array.from(new Set([...Object.keys(compareLeftData), ...Object.keys(compareRightData)])).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    const rows = allKeys.map((key) => {
      const leftValue = compareLeftData[key] ?? '';
      const rightValue = compareRightData[key] ?? '';
      let kind: CompareKind = 'unchanged';
      if (leftValue === '' && rightValue !== '') kind = 'added';
      else if (leftValue !== '' && rightValue === '') kind = 'removed';
      else if (leftValue !== rightValue) kind = 'modified';
      return { key, leftValue: leftValue || '-', rightValue: rightValue || '-', kind };
    });
    return showOnlyDifferences ? rows.filter((row) => row.kind !== 'unchanged') : rows;
  }, [compareLeftData, compareRightData, showOnlyDifferences]);

  const compareTree = useMemo(() => buildCompareTree(compareRows), [compareRows]);
  const compareSummary = useMemo(
    () =>
      compareRows.reduce(
        (acc, row) => {
          if (row.kind === 'added') acc.added += 1;
          else if (row.kind === 'removed') acc.removed += 1;
          else if (row.kind === 'modified') acc.modified += 1;
          return acc;
        },
        { added: 0, removed: 0, modified: 0 }
      ),
    [compareRows]
  );

  useEffect(() => {
    const nextOpen: Record<string, boolean> = {};
    compareTree.forEach((node) => {
      if (node.children.length > 0) nextOpen[node.path] = false;
    });
    setOpenCompareNodes(nextOpen);
  }, [compareTree]);

  const getCompareVersionLabel = (versionId: string, fallback: string) => {
    if (!versionId) return t(fallback);
    const selected = versions.find((version) => getVersionKey(version) === versionId);
    const source = selected?.version_id ?? versionId;
    const { versionNumber } = parseVersionIdForDisplay(source);
    return versionNumber || selected?.version_name || versionId || t(fallback);
  };

  const getCompareRowBgClass = (kind: CompareKind) => {
    if (kind === 'added') return 'bg-success/10';
    if (kind === 'removed') return 'bg-danger/10';
    if (kind === 'modified') return 'bg-warning/10';
    return 'bg-content1';
  };

  const runCompare = async (leftId: string, rightId: string) => {
    if (!leftId || !rightId) {
      CtyShowToast(checkType.warning, t('Please select two versions to compare'));
      return;
    }
    if (leftId === rightId) {
      CtyShowToast(checkType.warning, t('Please choose different versions'));
      return;
    }
    const leftVersion = versions.find((version) => getVersionKey(version) === leftId);
    const rightVersion = versions.find((version) => getVersionKey(version) === rightId);
    if (!leftVersion || !rightVersion) {
      CtyShowToast(checkType.error, t('Selected version metadata is unavailable.'));
      return;
    }
    try {
      setIsComparingVersions(true);
      const [leftData, rightData] = await Promise.all([
        fetchVersionData(leftVersion, leftId),
        fetchVersionData(rightVersion, rightId),
      ]);
      setCompareLeftData(leftData);
      setCompareRightData(rightData);
    } catch (error: unknown) {
      const message =
        (error as { data?: { message?: string }; message?: string })?.data?.message ??
        (error as { message?: string })?.message ??
        t('Failed to compare versions');
      CtyShowToast(checkType.error, String(message));
      setCompareLeftData({});
      setCompareRightData({});
    } finally {
      setIsComparingVersions(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !autoRunKey) return;
    const leftId = initialLeftVersionId ?? '';
    const rightId = initialRightVersionId ?? '';
    if (leftId && rightId) {
      void runCompare(leftId, rightId);
    }
  }, [autoRunKey, isOpen, initialLeftVersionId, initialRightVersionId]);

  const leftColumnLabel = getCompareVersionLabel(compareLeftVersionId, 'Version A');
  const rightColumnLabel = getCompareVersionLabel(compareRightVersionId, 'Version B');
  const getNodeDisplayName = (nodeName: string) => {
    if (getNodeDisplayNameProp) {
      return getNodeDisplayNameProp(nodeName);
    }
    return nodeName;
  };

  const handleToggleNode = (path: string) => {
    setOpenCompareNodes((prev) => ({
      ...prev,
      [path]: !(prev[path] ?? true),
    }));
  };

  const renderGroupNode = (node: CompareTreeNode, depth: number, keyCellPadding: number, isNodeOpen: boolean) => (
    <div key={node.path} className="border-t">
      <button
        type="button"
        className={`grid grid-cols-12 px-3 py-2 text-sm cursor-pointer w-full text-left ${getCompareRowBgClass(node.kind)}`}
        onClick={() => handleToggleNode(node.path)}
      >
        <div className="col-span-4 font-semibold break-words flex items-center gap-2" style={{ paddingLeft: `${keyCellPadding}px` }} title={node.path}>
          <span className="text-foreground-500 inline-flex items-center">
            {isNodeOpen ? <Icon icon="icon-park-outline:up-c" width={14} height={14} /> : <Icon icon="icon-park-outline:down-c" width={14} height={14} />}
          </span>
          <span>{getNodeDisplayName(node.name)}</span>
        </div>
        <div className="col-span-8 text-xs text-foreground-500 flex items-center">{t('Group')}</div>
      </button>
      {isNodeOpen ? <div>{renderTreeNodes(node.children, depth + 1)}</div> : null}
    </div>
  );

  const renderLeafNode = (node: CompareTreeNode, keyCellPadding: number) => (
    <div key={node.path} className={`grid grid-cols-12 px-3 py-2 text-sm border-t ${getCompareRowBgClass(node.kind)}`}>
      <div className="col-span-4 font-medium break-words" style={{ paddingLeft: `${keyCellPadding}px` }} title={node.path}>
        {getNodeDisplayName(node.name)}
      </div>
      <div className="col-span-4 text-foreground-700 break-words">{node.leftValue ?? '-'}</div>
      <div className="col-span-4 text-foreground-700 break-words">{node.rightValue ?? '-'}</div>
    </div>
  );

  const renderTreeNode = (node: CompareTreeNode, depth: number) => {
    const keyCellPadding = depth * 16 + 8;
    const isNodeOpen = openCompareNodes[node.path] ?? true;
    if (node.children.length > 0) {
      return renderGroupNode(node, depth, keyCellPadding, isNodeOpen);
    }
    return renderLeafNode(node, keyCellPadding);
  };

  const renderTreeNodes = (nodes: CompareTreeNode[], depth = 0): React.ReactNode =>
    nodes.map((node) => renderTreeNode(node, depth));

  return (
    <Modal isOpen={isOpen} onClose={onClose} placement="center" backdrop="blur" className="max-w-6xl w-full">
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <span className="text-base font-semibold">{title ?? t('Version Comparison')}</span>
              <span className="text-xs text-foreground-500">{description ?? t('Compare two versions to identify added, removed, and modified fields.')}</span>
            </ModalHeader>
            <ModalBody className="overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Select
                  placeholder={t('Select version')}
                  variant="bordered"
                  radius="sm"
                  aria-label="version-compare-left"
                  selectedKeys={compareLeftVersionId ? [compareLeftVersionId] : []}
                  onSelectionChange={(keys) => {
                    if (keys === 'all') return;
                    setCompareLeftVersionId((Array.from(keys)[0] as string) ?? '');
                  }}
                >
                  {versions.map((version) => (
                    <SelectItem key={getVersionKey(version)} value={getVersionKey(version)} textValue={getVersionKey(version)}>
                      {getVersionKey(version)}
                    </SelectItem>
                  ))}
                </Select>
                <Select
                  placeholder={t('Select version')}
                  variant="bordered"
                  radius="sm"
                  aria-label="version-compare-right"
                  selectedKeys={compareRightVersionId ? [compareRightVersionId] : []}
                  onSelectionChange={(keys) => {
                    if (keys === 'all') return;
                    setCompareRightVersionId((Array.from(keys)[0] as string) ?? '');
                  }}
                >
                  {versions.map((version) => (
                    <SelectItem key={getVersionKey(version)} value={getVersionKey(version)} textValue={getVersionKey(version)}>
                      {getVersionKey(version)}
                    </SelectItem>
                  ))}
                </Select>
              </div>
              <div className="flex flex-wrap justify-between gap-2 mt-1">
                <Checkbox isSelected={showOnlyDifferences} onValueChange={setShowOnlyDifferences}>{t('Filter Differences')}</Checkbox>
                <div className="flex items-center gap-2">
                  <Chip size="sm" color="success" variant="flat">{t('Added')}: {compareSummary.added}</Chip>
                  <Chip size="sm" color="danger" variant="flat">{t('Removed')}: {compareSummary.removed}</Chip>
                  <Chip size="sm" color="warning" variant="flat">{t('Modified')}: {compareSummary.modified}</Chip>
                </div>
              </div>
              <div className="border rounded-md overflow-hidden">
                <div className="grid grid-cols-12 bg-default-100 text-sm font-medium px-3 py-2">
                  <div className="col-span-4">{t('Key')}</div>
                  <div className="col-span-4">{leftColumnLabel}</div>
                  <div className="col-span-4">{rightColumnLabel}</div>
                </div>
                <div className="max-h-[40vh] overflow-y-auto">
                  {isComparingVersions ? (
                    <div className="flex justify-center p-6"><span className="text-sm text-foreground-500">{t('Comparing versions...')}</span></div>
                  ) : compareTree.length > 0 ? (
                    <div>{renderTreeNodes(compareTree)}</div>
                  ) : (
                    <div className="p-6 text-sm text-foreground-500 text-center">{t('No differences found for selected versions.')}</div>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter>
              <div className="flex gap-2 w-full justify-end">
                <CtyButton variant="light" onPress={onClose}>{t('Close')}</CtyButton>
                <CtyButton color="primary" onPress={() => void runCompare(compareLeftVersionId, compareRightVersionId)} isLoading={isComparingVersions}>
                  {t('Compare Versions')}
                </CtyButton>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}

export default VersionCompareModal;
